import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { apiFetchAuth } from '../../api/client';
import './StyleBetaReward.css';

const TERMINAL = new Set(['earned', 'active', 'scheduled', 'expired']);

function formatDate(value, language) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'ko-KR', {
      year: 'numeric', month: 'long', day: 'numeric',
    }).format(new Date(value));
  } catch { return ''; }
}

export default function StyleBetaReward() {
  const { t, i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || 'ko').startsWith('en') ? 'en' : 'ko';
  const [reward, setReward] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer;
    let attempts = 0;
    const load = async () => {
      attempts += 1;
      try {
        const payload = await apiFetchAuth('/api/style-beta/reward');
        if (cancelled) return;
        setReward(payload); setFailed(false);
        if (!TERMINAL.has(payload?.status) && attempts < 8) timer = window.setTimeout(load, 1200);
      } catch {
        if (cancelled) return;
        if (attempts < 4) timer = window.setTimeout(load, 1200);
        else setFailed(true);
      }
    };
    load();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, []);

  const state = failed ? 'error' : (reward?.status || 'loading');
  const copyState = ['earned', 'active', 'scheduled', 'expired'].includes(state) ? state
    : (state === 'loading' || state === 'issued' ? 'processing' : 'not_found');
  const dates = useMemo(() => ({
    starts: formatDate(reward?.starts_at, language),
    expires: formatDate(reward?.expires_at, language),
  }), [language, reward?.expires_at, reward?.starts_at]);

  return (
    <main className="style-reward-page">
      <Helmet><title>{t('style_beta_reward.meta_title')}</title></Helmet>
      <section className={`style-reward-card is-${copyState}`} aria-live="polite">
        <div className="style-reward-mark" aria-hidden="true">
          {copyState === 'processing' ? <span className="style-reward-spinner" /> : <span>{copyState === 'not_found' ? '!' : '✓'}</span>}
        </div>
        <p className="style-reward-eyebrow">{t('style_beta_reward.eyebrow')}</p>
        <h1>{t(`style_beta_reward.${copyState}.title`)}</h1>
        <p className="style-reward-description">{t(`style_beta_reward.${copyState}.body`)}</p>

        {(copyState === 'earned' || copyState === 'active' || copyState === 'scheduled') && <div className="style-reward-benefits">
          <article><span>30</span><div><strong>{t('style_beta_reward.benefits.style_title')}</strong><small>{copyState === 'active' && dates.expires ? t('style_beta_reward.benefits.until', { date: dates.expires }) : t('style_beta_reward.benefits.after_launch')}</small></div></article>
          <article><span>5</span><div><strong>{t('style_beta_reward.benefits.chemistry_title')}</strong><small>{copyState === 'active' ? t('style_beta_reward.benefits.remaining', { count: reward?.chemistry_uses ?? 5 }) : t('style_beta_reward.benefits.on_activation')}</small></div></article>
        </div>}

        {copyState === 'scheduled' && dates.starts && <p className="style-reward-schedule">{t('style_beta_reward.scheduled.starts', { date: dates.starts })}</p>}
        <div className="style-reward-actions">
          {copyState === 'processing' ? <button type="button" onClick={() => window.location.reload()}>{t('style_beta_reward.actions.refresh')}</button>
            : <Link className="style-reward-primary" to="/">{t('style_beta_reward.actions.home')}</Link>}
          <Link className="style-reward-secondary" to="/mypage">{t('style_beta_reward.actions.mypage')}</Link>
        </div>
        <p className="style-reward-note">{t('style_beta_reward.note')}</p>
      </section>
    </main>
  );
}
